import { Injectable } from "@angular/core";
import { ProductTotalCalculator } from "../product-total/product-total-calculator";
import { IndividualProductCalculationStrategy } from "../product-total/individual-product-calculation-strategy";
import { BundleProductCalculationStrategy } from "../product-total/bundle-product-calculation-strategy";
import { BundleInnerProductCalculationStrategy } from "../product-total/bundle-inner-product-calculation-strategy";
import { IProductCalculationContext, IOrderTotalCalculationContext } from "../models/product-calculation-context.model";
import { ProductsCalculationUtilsService } from "./products-calculation-utils.service";
import { TypeIds } from "src/app/inventory/sales-order/constants/constants";

@Injectable({
    providedIn: "root",
})
export class ProductTotalCalculationService {
    private calculator: ProductTotalCalculator;

    constructor(private productsCalculationUtilsService: ProductsCalculationUtilsService) {
        this.calculator = new ProductTotalCalculator(new IndividualProductCalculationStrategy(productsCalculationUtilsService));
    }

    calculateProductTotal(context: IProductCalculationContext, isBundle: boolean, isBundleInnerProduct: boolean) {
        if (isBundle) {
            this.calculator.setStrategy(new BundleProductCalculationStrategy(this.productsCalculationUtilsService));
        } else if (isBundleInnerProduct) {
            this.calculator.setStrategy(new BundleInnerProductCalculationStrategy(this.productsCalculationUtilsService));
        } else {
            this.calculator.setStrategy(new IndividualProductCalculationStrategy(this.productsCalculationUtilsService));
        }
        this.calculator.calculateTotal(context);
    }

    // calculateSubtotal(productForm) {
    //     const productsArray = productForm.get("products").value;
    //     const subtotal = productsArray.reduce((accumulator, product) => {
    //         accumulator += product.typeId == TypeIds.BundleTypeId ? 0 : product.totalAmount ?? 0;
    //         if (product.bundleProducts && product.bundleProducts.length > 0) {
    //             accumulator += product.bundleProducts.reduce((bundleAccumulator, bundleProduct) => {
    //                 return bundleAccumulator + (bundleProduct.totalAmount ?? 0);
    //             }, 0);
    //         }
    //         return accumulator;
    //     }, 0);
    //     return subtotal;
    // }

    calculateSubtotal(productForm) {
        const productsArray = productForm.get("products").value;
        return productsArray.reduce(
            (totals, product) => {
                if (product.typeId !== TypeIds.BundleTypeId) {
                    totals.totalBaseSubTotal += product.baseSubTotal ?? 0;
                    totals.totalSubTotal += product.subTotal ?? 0;
                    totals.productTotalTaxAmount += product.taxAmount ?? 0;
                    totals.productTotalDiscountAmount += product.discountAmount ?? 0;
                    totals.productTotalHandlingFeeAmount += product.handlingFeeAmount ?? 0;
                    totals.totalAmount += product.totalAmount ?? 0;
                }
                if (product.bundleProducts && product.bundleProducts.length > 0 && product.typeId !== TypeIds.KitTypeId) {
                    product.bundleProducts.forEach((bundleProduct) => {
                        totals.totalBaseSubTotal += bundleProduct.baseSubTotal ?? 0;
                        totals.totalSubTotal += bundleProduct.subTotal ?? 0;
                        totals.productTotalTaxAmount += bundleProduct.taxAmount ?? 0;
                        totals.productTotalDiscountAmount += bundleProduct.discountAmount ?? 0;
                        totals.productTotalHandlingFeeAmount += bundleProduct.handlingFeeAmount ?? 0;
                        totals.totalAmount += bundleProduct.totalAmount ?? 0;
                    });
                }
                return totals;
            },
            {
                totalBaseSubTotal: 0,
                totalSubTotal: 0,
                productTotalTaxAmount: 0,
                productTotalDiscountAmount: 0,
                productTotalHandlingFeeAmount: 0,
                totalAmount: 0,
            },
        );
    }

    calculateTotalTaxAmount(productForm) {
        const productsArray = productForm.get("products").value;
        const subtotal = productsArray.reduce((accumulator, product) => {
            accumulator += product.typeId == TypeIds.BundleTypeId ? 0 : (product.taxAmount ?? 0);
            if (product.bundleProducts && product.bundleProducts.length > 0) {
                accumulator += product.bundleProducts.reduce((bundleAccumulator, bundleProduct) => {
                    return bundleAccumulator + (bundleProduct.taxAmount ?? 0);
                }, 0);
            }
            return accumulator;
        }, 0);
        return subtotal;
    }

    calculateOrderTotal(orderTotalCalculationContext: IOrderTotalCalculationContext) {
        const orderSubtotal = orderTotalCalculationContext.productForm.get("orderSubtotal")?.value ?? 0;
        const orderBaseSubtotal = orderTotalCalculationContext.productForm.get("orderBaseSubtotal")?.value ?? 0;
        const productTotalTaxAmount = orderTotalCalculationContext.productForm.get("productTotalTaxAmount")?.value ?? 0;
        const productTotalDiscountAmount = orderTotalCalculationContext.productForm.get("productTotalDiscountAmount")?.value ?? 0;
        const productTotalHandlingFeeAmount = orderTotalCalculationContext.productForm.get("productTotalHandlingFeeAmount")?.value ?? 0;

        const discountType = orderTotalCalculationContext.productForm.get("discountType")?.value ?? null;
        const discountValue = orderTotalCalculationContext.productForm.get("discountRate")?.value ?? 0;
        const shippingCharge = orderTotalCalculationContext.productForm.get("shippingCharge")?.value ?? 0;

        let totalAmount = orderBaseSubtotal + productTotalHandlingFeeAmount;
        let discount = 0;
        let tax = 0;
        let totalAdditionalFee = 0;
        let additionalDiscountAmount = 0;
        
         // Step 1: Subtract Discount
        if (orderTotalCalculationContext.checkedFields?.productDiscountType?.enabledInForm) {
            discount = productTotalDiscountAmount;
        } else {
            discount = this.productsCalculationUtilsService.calculateDiscount(discountType?.name, discountValue, totalAmount);
        }
        if (orderTotalCalculationContext.isDiscountEnabled) {
            totalAmount -= discount;
        }

        // Step 2: Add Tax
        tax = productTotalTaxAmount;
        if (orderTotalCalculationContext.isTaxEnabled) {
            totalAmount += tax;
        }

        // Step 3: Add Additional Fee
        totalAdditionalFee = this.productsCalculationUtilsService.calculateHandlingFee(orderTotalCalculationContext.productForm?.controls["handlingFeeType"]?.value?.type?.name, orderTotalCalculationContext.productForm?.controls["handlingFeeRate"]?.value, totalAmount, orderTotalCalculationContext.checkedFields?.handlingFeesType?.enabledInForm);
        totalAmount += totalAdditionalFee;

        // Step 4: Subtract Additional Discount
        additionalDiscountAmount = this.productsCalculationUtilsService.calculateAdditionalDiscount(orderTotalCalculationContext.productForm, totalAmount);

        // Step 5: Add Shipping Charge
        totalAmount = totalAmount - additionalDiscountAmount + shippingCharge;
        
        orderTotalCalculationContext.productForm.patchValue(
            {
                discountAmount: discount,
                taxAmount: tax,
                handlingFeeAmount: totalAdditionalFee,
                additionalDiscountAmount: additionalDiscountAmount,
                totalAmount: totalAmount,
            },
            { onlySelf: true, emitEvent: false },
        );
        return orderTotalCalculationContext.productForm;
    }
}
